import cx from 'classnames';
import Link from 'next/link';
import useTranslation from 'next-translate/useTranslation';
import { FunctionComponent } from 'react';

import useLinkProps from '../../../../hooks/use-link-props';
import SecondaryLink from '../../../ui/links/secondary';
import ListHeader from '../../../ui/v2/vehicle-list/list-header';
import ListItem from '../../../ui/v2/vehicle-list/list-item';
import { useAtom } from 'jotai';
import { modelAtom, versionAtom } from '../../../../hooks/use-picker-selection';
import {
  currentSavedCarAtom,
  selectVehicleStepAtom,
  unsavedInformationAtom,
  vehiclePickerOpen,
} from '../../../../state/vehicle';
import { useRouter } from 'next/router';

type TFilteredList = {
  headerTag?: keyof JSX.IntrinsicElements;
  itemTag?: keyof JSX.IntrinsicElements;
  values: TListModelsOrVersions;
  isAllDisabled?: boolean;
  displayList?: boolean;
  customClickFunction?: boolean;
  noResultsLink?: boolean;
  numberOfColumns?: number;
  listType?: 'MODELS' | 'VERSIONS' | undefined;
};

const FilteredList: FunctionComponent<TFilteredList> = ({
  headerTag = 'h2',
  itemTag = 'h3',
  values,
  isAllDisabled = false,
  displayList = false,
  numberOfColumns = 1,
  customClickFunction = false,
  listType,
  noResultsLink = true,
}) => {
  const { t } = useTranslation();
  const { locale, push, query, route } = useRouter();
  const getLink = useLinkProps();
  const totalOfRows = Object.values(values).length;
  const [, setModel] = useAtom(modelAtom);
  const [, setVersion] = useAtom(versionAtom);
  const [, setSelectVehicleStep] = useAtom(selectVehicleStepAtom);
  const [, setOpen] = useAtom(vehiclePickerOpen);
  const [, setCurrentSavedCar] = useAtom(currentSavedCarAtom);
  const [, setUnsavedInformation] = useAtom(unsavedInformationAtom);

  const customOnClickFunction = (selectedValue) => {
    if (!customClickFunction) return;

    if (listType === 'MODELS') {
      setModel(selectedValue);
      if (selectedValue.have_versions) {
        setSelectVehicleStep('SELECT_VERSION');
        setUnsavedInformation(false);
        return;
      }
    }

    if (listType === 'VERSIONS') {
      setVersion(selectedValue);
    }

    if (route !== '/') {
      const {
        reference,
        category,
        subCategory,
        priceFrom,
        priceTo,
        page,
        ...restTokeep
      } = query;

      const link = getLink('reference', {
        ...restTokeep,
        ...(reference && { reference }),
        ...(priceFrom && { priceFrom }),
        ...(category && { category }),
        ...(subCategory && { subCategory }),
        ...(priceTo && { priceTo }),
        ...(page && { page }),
        ...(selectedValue && { vehicle: selectedValue.slug }),
      });

      setUnsavedInformation(false);
      setOpen(false);
      setCurrentSavedCar(null);
      setSelectVehicleStep(null);
      push(link.href, link.as, { shallow: true, scroll: true });
    } else {
      setSelectVehicleStep('SELECT_PART');
      setUnsavedInformation(false);
      return;
    }
  };

  if (Object.values(values).length === 0) {
    return (
      <div className={cx('text-center', 'mt-12')}>
        <p className={cx('mb-6')}>{t('common:noresults')}</p>
        {noResultsLink && (
          <Link
            {...getLink('brands')}
            passHref
            shallow
            scroll
            prefetch={false}
            legacyBehavior
          >
            <SecondaryLink className={cx('inline-block')}>
              {t('vehicle:backtobrands')}
            </SecondaryLink>
          </Link>
        )}
      </div>
    );
  }

  return (
    <div className={cx()}>
      {Object.entries(values).map(([key, links], index) => (
        <div
          key={key}
          className={cx(
            'max-lg:py-10 lg:py-10 first:pt-0',
            index < totalOfRows - 1 ? 'border-b border-neutral-30' : ''
          )}
        >
          <ListHeader as={headerTag}>{key}</ListHeader>
          <ul
            className={cx(
              'grid gap-4',
              displayList
                ? 'grid-cols-1 items-center'
                : 'lg:grid-cols-[repeat(auto-fill,calc(20%-16px))] '
            )}
          >
            {links.map((model) => (
              <ListItem
                itemTag={itemTag}
                value={model}
                key={model.id}
                isAllDisabled={isAllDisabled}
                customClickFunction={customClickFunction}
                onClick={() => {
                  customOnClickFunction(model);
                }}
              />
            ))}
          </ul>
        </div>
      ))}
    </div>
  );
};

export default FilteredList;
